import type { layout } from "@app/domain";
import { queueVariantOf, type SeatInfo, type Snapshot } from "@app/protocol";
import { useEffect, useState } from "react";
import { Slot, Stage } from "../canvas/Stage";
import { OwnerButtons } from "../components/LobbyOwner";
import { Reconnecting } from "../components/Overlays";
import { SeatPods } from "../components/SeatPods";
import { seatSlots } from "../components/seatOrder";
import { TopBar, useBack } from "../components/TopBar";
import { useStalled } from "../components/useStalled";
import { useT } from "../i18n/context";
import { lobbySeatLook, ruleLabels, targetLabel } from "./lobbyText";

/**
 * Seconds left before the start, ticking here between snapshots: the server
 * sends `countdownMs` once, as what was left when it went out.
 */
function useCountdown(snapshot: Snapshot): number {
  const ms = snapshot.table.countdownMs;
  const [mark, setMark] = useState(() => ({ snapshot, at: Date.now() }));
  if (mark.snapshot !== snapshot) setMark({ snapshot, at: Date.now() });
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (ms === null) return;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [ms]);
  if (ms === null) return 0;
  return Math.max(0, Math.ceil((ms - Math.max(0, now - mark.at)) / 1000));
}

/** The line under the settings at a table "Find players" fills; phones get the short one. */
function queueLine(tableId: string, dense: boolean) {
  if (queueVariantOf(tableId) === null) return dense ? "match.seatsOpen.short" : "match.seatsOpen";
  return dense ? "match.anywhere.short" : "match.anywhere";
}

/** On the middle of the table (design 3): the countdown or who is seated, the settings, the owner's buttons. */
function Centre({
  snapshot,
  lastError,
  dense,
  row,
}: {
  snapshot: Snapshot;
  lastError: string | null;
  dense: boolean;
  /** Phone landscape: the owner's buttons side by side; the narrow portrait table stacks them. */
  row: boolean;
}) {
  const { t, err } = useT();
  const { table, you } = snapshot;
  const taken = table.seats.filter((seat) => seat !== null).length;
  const humans = table.seats.filter((seat) => seat !== null && !seat.bot).length;
  const stalled = useStalled(humans, 4 - taken);
  const seconds = useCountdown(snapshot);
  const pill = lastError
    ? { text: err(lastError), edge: "border-danger-soft/70" }
    : table.status === "countdown"
      ? { text: t("lobby.countdown", { seconds }), edge: "border-success/60" }
      : { text: t("lobby.seated", { count: taken }), edge: "border-edge" };
  const summary = [
    t("lobby.settings", {
      variant: t(`variant.${table.settings.variant}`),
      target: targetLabel(t, table.settings),
      tempo: t(`tempo.${table.settings.tempo}`),
    }),
    ...ruleLabels(t, table.settings),
  ].join(" · ");
  const size = dense ? "h-8 px-3.5 text-[12px]" : "h-12 px-5 text-[14px]";
  return (
    <div
      className={`flex h-full flex-col items-center justify-center text-center ${dense ? "gap-1.5" : "gap-3"}`}
    >
      <span
        aria-live="polite"
        className={`flex items-center whitespace-nowrap rounded-pill border-[1.5px] bg-panel-strong font-bold shadow-panel backdrop-blur-[10px] ${size} ${pill.edge}`}
      >
        {pill.text}
      </span>
      <span
        className={`text-legible font-bold tracking-[0.02em] text-ink/90 ${dense ? "whitespace-nowrap text-[11px]" : "text-[13px]"}`}
      >
        {summary}
      </span>
      {table.queue ? (
        <span
          className={`text-legible font-bold text-ink/80 ${dense ? "whitespace-nowrap text-[11px]" : "text-[12px]"}`}
        >
          {t(queueLine(table.id, dense))}
        </span>
      ) : null}
      {table.owner === you.userId ? (
        <OwnerButtons
          table={table}
          emptySeats={4 - taken}
          stalled={stalled}
          size={size}
          dense={dense}
          row={row}
        />
      ) : null}
    </div>
  );
}

/** A seated table before the start: seats, the countdown, the owner's buttons. See docs/ekranlar/lobby.md. */
export function Lobby({
  snapshot,
  measure,
  lastError,
}: {
  snapshot: Snapshot;
  measure: layout.Measure;
  lastError: string | null;
}) {
  const { t } = useT();
  const { table, you } = snapshot;
  const canvas = measure.canvas;
  const seated = you.seat !== null;
  const back = useBack("lobby", seated, table.queue);
  const viewer = {
    userId: you.userId,
    seated,
    owner: table.owner === you.userId,
    ownerId: table.owner,
  };
  return (
    <Stage measure={measure} scene={table.scene} overlay={<Reconnecting seated={seated} />}>
      <Slot rect={canvas.elements.topBar} className="z-20" label={t("lobby.title")}>
        <TopBar
          state={measure.state}
          back={back}
          watchers={table.spectators.length}
          seated={seated}
          variant={table.settings.variant}
        />
      </Slot>
      <SeatPods
        variant={table.settings.variant}
        status={table.status}
        gifts={table.gifts}
        seats={table.seats}
        slots={seatSlots(you.seat)}
        canvas={canvas}
        look={(seat: SeatInfo | null, index: number) => lobbySeatLook(seat, index, viewer, t)}
      />
      <Slot rect={canvas.elements.trick} className="z-20">
        <Centre
          snapshot={snapshot}
          lastError={lastError}
          dense={measure.state !== "wide"}
          row={measure.state === "phoneLandscape"}
        />
      </Slot>
    </Stage>
  );
}
